import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Users, Contact, Ticket, Loader2, X, CornerDownLeft, Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { useBusiness } from "@/contexts/BusinessContext";
import { resolveAsset, hideOnError } from "@/lib/assets";

/**
 * Global search: type a name / phone / email / lead-# and results appear
 * instantly in a dropdown, debounced. Searches across EVERY business the user
 * can access — clicking a result auto-switches the active business context if
 * it belongs to a different business, then navigates into the record.
 */
export default function GlobalSearch() {
  const { activeId, businesses, switchBusiness } = useBusiness();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = q.trim();
    if (!term) { setResults(null); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        // scope=all forces the backend to search every business the user can
        // access, ignoring the currently-active business.
        const r = await api.get("/search/global", { params: { q: term, scope: "all" } });
        setResults(r.data);
        setOpen(true);
      } catch {
        setResults({ leads: [], clients: [], tickets: [], total: 0 });
      } finally { setLoading(false); }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = useCallback((path, targetBizId) => {
    // Auto-switch business context if the record lives in a different business.
    if (targetBizId && targetBizId !== activeId && businesses.some((b) => b.id === targetBizId)) {
      switchBusiness(targetBizId);
    }
    setOpen(false); setQ(""); setResults(null);
    navigate(path);
  }, [navigate, activeId, businesses, switchBusiness]);

  const total = results ? (results.leads?.length || 0) + (results.clients?.length || 0) + (results.tickets?.length || 0) : 0;

  const highlight = (text) => {
    if (!text) return text;
    const term = q.trim();
    if (!term) return text;
    const idx = String(text).toLowerCase().indexOf(term.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {String(text).slice(0, idx)}
        <mark className="bg-accent/30 text-inherit rounded px-0.5">{String(text).slice(idx, idx + term.length)}</mark>
        {String(text).slice(idx + term.length)}
      </>
    );
  };

  const BusinessBadge = ({ name, logo }) => {
    if (!name) return null;
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-secondary/60 px-1.5 py-0.5 text-[10px] font-medium border border-border/60">
        {logo ? (
          <img src={resolveAsset(logo)} onError={hideOnError} alt="" className="h-3 w-3 object-contain rounded-sm" />
        ) : (
          <Building2 className="h-3 w-3 opacity-70" />
        )}
        <span className="truncate max-w-[100px]">{name}</span>
      </span>
    );
  };

  return (
    <div className="relative w-full" ref={boxRef} data-testid="global-search-wrap">
      <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => q.trim() && setOpen(true)}
        placeholder="Search across ALL businesses — name, phone, email, lead #…"
        className="pl-9 pr-8 h-9"
        data-testid="global-search"
      />
      {loading ? (
        <Loader2 className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" />
      ) : q ? (
        <button
          type="button"
          onClick={() => { setQ(""); setResults(null); setOpen(false); }}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          data-testid="global-search-clear"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}

      {open && q.trim() && (
        <div
          className="absolute left-0 right-0 mt-2 rounded-xl border border-border bg-card shadow-xl z-50 overflow-hidden max-h-[70vh] overflow-y-auto"
          data-testid="global-search-results"
        >
          {loading && total === 0 && (
            <div className="px-4 py-6 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Searching all businesses…
            </div>
          )}
          {!loading && total === 0 && (
            <div className="px-4 py-6 text-sm text-muted-foreground text-center" data-testid="global-search-empty">
              No results for &quot;{q}&quot; across your businesses
            </div>
          )}

          {results?.leads?.length > 0 && (
            <Section title="Leads" icon={Users}>
              {results.leads.map((l) => (
                <ResultRow
                  key={l.id}
                  testId={`gs-lead-${l.id}`}
                  onClick={() => go(`/leads/${l.id}`, l.business_id)}
                  title={highlight(l.name || "Unnamed")}
                  badge={<BusinessBadge name={l.business_name} logo={l.business_logo} />}
                  meta={[l.lead_number, l.phone, l.email].filter(Boolean).map((x, i) => (
                    <span key={i}>{highlight(x)}</span>
                  ))}
                />
              ))}
            </Section>
          )}

          {results?.clients?.length > 0 && (
            <Section title="Clients" icon={Contact}>
              {results.clients.map((c) => (
                <ResultRow
                  key={c.id}
                  testId={`gs-client-${c.id}`}
                  onClick={() => go(`/clients`, c.business_id)}
                  title={highlight(c.client_name || "Client")}
                  badge={<BusinessBadge name={c.business_name} logo={c.business_logo} />}
                  meta={[c.primary_mobile, c.email].filter(Boolean).map((x, i) => (
                    <span key={i}>{highlight(x)}</span>
                  ))}
                />
              ))}
            </Section>
          )}

          {results?.tickets?.length > 0 && (
            <Section title="Tickets" icon={Ticket}>
              {results.tickets.map((t) => (
                <ResultRow
                  key={t.id}
                  testId={`gs-ticket-${t.id}`}
                  onClick={() => go(`/tickets/${t.id}`, t.business_id)}
                  title={highlight(t.subject || t.ticket_number)}
                  badge={<BusinessBadge name={t.business_name} logo={t.business_logo} />}
                  meta={[t.ticket_number, t.status].filter(Boolean).map((x, i) => (
                    <span key={i}>{highlight(x)}</span>
                  ))}
                />
              ))}
            </Section>
          )}

          {total > 0 && (
            <div className="px-3 py-2 border-t border-border text-[10px] text-muted-foreground flex items-center gap-1">
              <CornerDownLeft className="h-3 w-3" /> Click a result — we&apos;ll switch to that business automatically
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
        <Icon className="h-3 w-3" /> {title}
      </div>
      {children}
    </div>
  );
}

function ResultRow({ title, meta, badge, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-4 py-2.5 hover:bg-secondary transition-colors flex flex-col gap-0.5 border-b border-border/50 last:border-0"
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium truncate">{title}</div>
        {badge}
      </div>
      {meta && meta.length > 0 && (
        <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
          {meta.map((m, i) => (
            <span key={i} className="flex items-center gap-2">
              {i > 0 && <span className="opacity-40">·</span>}
              {m}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}
